import queryString from 'query-string'
import { isString, sortBy } from 'lodash'

import { apiService, fetchingAPI } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'

// agent-gpt admits the orgs the caller may act for (their own, a hierarchy
// manager's child orgs, any org for Balto staff) with the AgentGPT Editor grant.
// The header carries the caller's home org; the target rides in body or query.
const GD_BASE = () => `${apiService.agentGpt}/google_drive_connections`
const USE_OWN_ORG = true

// Since QCER-1101 an org may hold several Drive connections, and the one
// duplicate agent-gpt still refuses is a Shared Drive another connection
// already allowlists. That 409 names the drive AND the connection holding it,
// and it is the operator's only route to fixing the config — handleResponse
// rejects a 409 with the parsed body, so surface the detail instead of
// swallowing it behind a generic failure.
const withDetail = (message, error) =>
  isString(error?.detail) ? `${message}: ${error.detail}` : message

// Every Google Drive connection the org holds, oldest first (QCER-1101 lifted
// the one-per-org cap, so this is genuinely a list). Creation order is stable, so rows keep their position across a save
// instead of reshuffling under the operator's cursor. Load errors propagate so
// the panel can tell "no connections yet" (show the empty state and its add
// path) apart from "load failed" (show the error) — otherwise a failed load
// looks like an empty org and invites a duplicate-create 409.
export const fetchGoogleDriveConnections =
  ({ organizationId }) =>
  async (dispatch) => {
    const rows = await fetchingAPI(
      `${GD_BASE()}?organization_id=${organizationId}`,
      'GET',
      dispatch,
      undefined,
      undefined,
      {},
      USE_OWN_ORG
    )
    return sortBy(rows, 'id')
  }

// Per-entry ingest state persisted by the Google Drive flow (QCER-1023
// mirror): what each configured drive/folder actually resolved to, how many
// documents the drive holds, when it last synced, and the last error. Load
// failures resolve to null, which the panel renders as its own "couldn't
// load" line (distinct from the never-synced empty state) — either way the
// config form stays usable when the state endpoint is unavailable.
export const fetchGoogleDriveSyncState =
  ({ connectionId }) =>
  async (dispatch) => {
    try {
      return await fetchingAPI(
        `${GD_BASE()}/${connectionId}/sync_state`,
        'GET',
        dispatch,
        undefined,
        undefined,
        {},
        USE_OWN_ORG
      )
    } catch (error) {
      return null
    }
  }

// The global service account's email — the identity customers add as Viewer
// on their Shared Drives. One SA authenticates everywhere (drive membership
// is the access boundary), so this is environment-level, not per-org. Resolves
// to null when the environment isn't bootstrapped yet (404) or the call fails;
// the panel renders its own "not configured" hint for that.
export const fetchGoogleDriveServiceAccount = () => async (dispatch) => {
  try {
    const account = await fetchingAPI(
      `${GD_BASE()}/service_account`,
      'GET',
      dispatch,
      undefined,
      undefined,
      {},
      USE_OWN_ORG
    )
    return account.client_email || null
  } catch (error) {
    return null
  }
}

export const createGoogleDriveConnection =
  ({ organizationId, label, workspaceDomain, allowlist, active }) =>
  async (dispatch) => {
    try {
      const created = await fetchingAPI(
        GD_BASE(),
        'POST',
        dispatch,
        JSON.stringify({
          organization_id: organizationId,
          label,
          workspace_domain: workspaceDomain,
          allowlist,
          active,
        }),
        undefined,
        {},
        USE_OWN_ORG
      )
      toast.success('Google Drive connection created')
      return created
    } catch (error) {
      toast.error(withDetail('Failed to create Google Drive connection', error))
      return null
    }
  }

export const updateGoogleDriveConnection =
  ({ connectionId, patch, successMessage }) =>
  async (dispatch) => {
    try {
      const updated = await fetchingAPI(
        `${GD_BASE()}/${connectionId}`,
        'PATCH',
        dispatch,
        JSON.stringify(patch),
        undefined,
        {},
        USE_OWN_ORG
      )
      toast.success(successMessage || 'Google Drive connection updated')
      return updated
    } catch (error) {
      toast.error(withDetail('Failed to update Google Drive connection', error))
      return null
    }
  }

// QCER-1024 mirror: remove ONE allowlist entry in a single agent-gpt
// operation, so config and data cannot drift apart. Omit folderId to remove
// the whole-drive entry (folder entries for the same drive are separate
// entries with their own requests). Documents are purged immediately only
// when the removal leaves no entry for the drive — otherwise the server
// defers the cleanup to the next nightly reconcile and says so via
// purge_deferred_to_reconcile; the toast is the operator's receipt of which
// of the two actually happened. A failure returns null so the caller keeps
// its confirmation open for a retry.
export const removeGoogleDriveAllowlistEntry =
  ({ connectionId, driveId, folderId }) =>
  async (dispatch) => {
    try {
      // stringify drops undefined values, so a whole-drive removal (no
      // folderId) sends only drive_id.
      const query = queryString.stringify({ drive_id: driveId, folder_id: folderId })
      const removed = await fetchingAPI(
        `${GD_BASE()}/${connectionId}/allowlist_entries?${query}`,
        'DELETE',
        dispatch,
        undefined,
        undefined,
        {},
        USE_OWN_ORG
      )
      const target = removed.whole_drive
        ? `drive ${removed.drive_id}`
        : `folder ${removed.folder_id} in ${removed.drive_id}`
      if (removed.purge_deferred_to_reconcile) {
        toast.success(
          `Removed ${target} — remaining entries still cover this drive; de-scoped documents are cleaned up by the next nightly sync`
        )
      } else {
        toast.success(`Removed ${target} — ${removed.objects_deleted} indexed objects purged`)
      }
      return removed
    } catch (error) {
      toast.error('Failed to remove the allowlist entry', { cause: error })
      return null
    }
  }

// Erase everything Google Drive has ingested for the org (agent-gpt's
// offboarding / GDPR purge). The connection ROW survives, stamped `purged_at`
// as the erasure marker — there is no delete-the-connection endpoint, so this
// is the panel's destructive action for a connection.
//
// Two server rules the panel has to respect rather than duplicate: the purge
// is refused with 409 while the connection is still active (deactivate first),
// and it is scoped so a sibling Drive connection sharing the org's Weaviate
// collection is never touched (QCER-1101 — it narrows to the drives THIS
// connection ingested whenever the org holds others, and refuses
// scope=entire_collection outright while a sibling still holds documents).
// SharePoint documents are never touched either way. The deleted-count toast
// is the operator's receipt that the erasure actually ran; a failure returns
// null so the caller keeps its confirmation open for a retry.
export const purgeGoogleDriveDocuments =
  ({ connectionId }) =>
  async (dispatch) => {
    try {
      const purged = await fetchingAPI(
        `${GD_BASE()}/${connectionId}/documents`,
        'DELETE',
        dispatch,
        undefined,
        undefined,
        {},
        USE_OWN_ORG
      )
      toast.success(`Documents erased — ${purged.objects_deleted} indexed objects purged`)
      return purged
    } catch (error) {
      toast.error('Failed to erase the Google Drive documents', { cause: error })
      return null
    }
  }
